import React from 'react';
import { 
  getReviewsData, 
  formatRating, 
  formatReviewCount 
} from '../../services/reviewsService';

interface ReviewsDisplayProps {
  className?: string;
  showText?: boolean;
  size?: 'small' | 'medium' | 'large';
}

// Configuration des styles par taille
const SIZE_CONFIG = {
  small: { text: 'text-sm', stars: 'text-yellow-400 text-sm' },
  medium: { text: 'text-base', stars: 'text-yellow-400 text-base' },
  large: { text: 'text-lg', stars: 'text-yellow-400 text-xl' }
} as const;

// Composant pour afficher les avis et notes Allocab
export function ReviewsDisplay(props: ReviewsDisplayProps): JSX.Element {
  const { className = '', showText = true, size = 'medium' } = props;
  const reviewsData = getReviewsData();
  const config = SIZE_CONFIG[size];

  const renderStars = () => {
    const fullStars = Math.floor(reviewsData.averageRating);
    const hasHalfStar = reviewsData.averageRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className={`flex items-center ${config.stars}`}>
        {/* Étoiles pleines */}
        {Array.from({ length: fullStars }, (_, i) => (
          <span key={`full-${i}`}>★</span>
        ))}
        
        {/* Demi-étoile optimisée */}
        {hasHalfStar && (
          <span key="half-star" className="relative inline-block w-4">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 absolute">
              <defs>
                <linearGradient id={`half-gradient-${size}`}>
                  <stop offset="50%" stopColor="currentColor"/>
                  <stop offset="50%" stopColor="transparent"/>
                </linearGradient>
              </defs>
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" 
                    fill={`url(#half-gradient-${size})`}/>
            </svg>
          </span>
        )}
        
        {/* Étoiles vides */}
        {Array.from({ length: emptyStars }, (_, i) => (
          <span key={`empty-${i}`} className="text-gray-300">★</span>
        ))}
      </div>
    );
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {renderStars()}
      
      {showText && (
        <div className={`${config.text} text-gray-700`}>
          <span className="font-semibold">
            {formatRating(reviewsData.averageRating)}/5
          </span>
          <span className="text-gray-500 ml-1">
            ({formatReviewCount(reviewsData.totalReviews)} avis)
          </span>
        </div>
      )}
    </div>
  );
}

// Composant compact pour afficher uniquement la note et le nombre d'avis
export function CompactReviewsDisplay({ className = '' }: { className?: string }): JSX.Element {
  const reviewsData = getReviewsData();

  return (
    <div className={`flex items-center space-x-1 text-sm text-gray-600 ${className}`}>
      <span className="text-yellow-400">★</span>
      <span className="font-medium">
        {formatRating(reviewsData.averageRating)}
      </span>
      <span>
        ({formatReviewCount(reviewsData.totalReviews)})
      </span>
    </div>
  );
}

